import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  WarningAmber as WarningIcon,
  Inventory2Outlined as ProductIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
} from '@mui/icons-material';

interface TreeMember {
  id: string;
  name: string;
  activeIngredientDetails: string[];
  // null = the group has no ingredients to compare against
  matchesGroupComposition: boolean | null;
}

interface TreeNode {
  id: string;
  name: string;
  archivedDate?: string;
  parentProductGroupId?: string;
  activeIngredientDetails: string[];
  memberProducts: TreeMember[];
}

interface ProductGroupTreeProps {
  includeArchived: boolean;
  search: string;
  // Bumped by the parent after any edit, to reload.
  refreshKey: number;
  onEdit: (groupId: string) => void;
}

const chipSx = { height: 20, fontSize: '0.6875rem', '& .MuiChip-label': { px: 0.75 } };
const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });

/**
 * The product-group hierarchy as a tree (parent → child groups → member
 * products), for checking it the way reports roll it up: a report on a group
 * covers its whole subtree. Flags the things that skew those reports — a
 * member whose ingredients differ from its group's, a leaf group with no
 * ingredients — and groups no top-level group reaches (a parent loop).
 */
const ProductGroupTree: React.FC<ProductGroupTreeProps> = ({ includeArchived, search, refreshKey, onEdit }) => {
  const auth = useAuth();
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Everything starts expanded; this tracks what the user closed.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!auth.isSignedIn || !auth.tokens?.accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/product-groups/tree?includeArchived=${includeArchived}`, {
          headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
        });
        if (!response.ok) throw new Error('Failed to load the product group tree');
        const data: TreeNode[] = await response.json();
        if (!cancelled) {
          setNodes(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [includeArchived, refreshKey, auth.isSignedIn, auth.tokens?.accessToken]);

  const { roots, childrenOf, unreachable } = useMemo(() => {
    const ids = new Set(nodes.map((n) => n.id));
    const childrenOf = new Map<string, TreeNode[]>();
    const roots: TreeNode[] = [];
    nodes.forEach((n) => {
      // A parent that isn't loaded (archived, with archived hidden) makes this a root.
      if (n.parentProductGroupId && ids.has(n.parentProductGroupId)) {
        childrenOf.set(n.parentProductGroupId, [...(childrenOf.get(n.parentProductGroupId) ?? []), n]);
      } else {
        roots.push(n);
      }
    });
    roots.sort(byName);
    childrenOf.forEach((list) => list.sort(byName));

    // Groups in a parent loop have no root above them, so a walk down from
    // the roots never reaches them (and never loops).
    const reached = new Set<string>();
    const stack = [...roots];
    while (stack.length > 0) {
      const n = stack.pop()!;
      reached.add(n.id);
      stack.push(...(childrenOf.get(n.id) ?? []));
    }
    const unreachable = nodes.filter((n) => !reached.has(n.id)).sort(byName);
    return { roots, childrenOf, unreachable };
  }, [nodes]);

  const subtreeProductCount = useMemo(() => {
    const counts = new Map<string, number>();
    const count = (n: TreeNode): number => {
      const cached = counts.get(n.id);
      if (cached !== undefined) return cached;
      counts.set(n.id, n.memberProducts.length); // placeholder guards a loop
      const total = n.memberProducts.length + (childrenOf.get(n.id) ?? []).reduce((sum, c) => sum + count(c), 0);
      counts.set(n.id, total);
      return total;
    };
    nodes.forEach(count);
    return counts;
  }, [nodes, childrenOf]);

  const query = search.trim().toLowerCase();
  const productMatches = (p: TreeMember) => !!query && p.name.toLowerCase().includes(query);

  // While searching: groups whose name or a member product matches, plus
  // their ancestors (to place them) and their whole subtree (to check it).
  const visible = useMemo(() => {
    if (!query) return null;
    const parentOf = new Map(nodes.map((n) => [n.id, n.parentProductGroupId]));
    const shown = new Set<string>();
    nodes
      .filter((n) => n.name.toLowerCase().includes(query) || n.memberProducts.some((p) => p.name.toLowerCase().includes(query)))
      .forEach((match) => {
        let id: string | undefined = match.id;
        const climbed = new Set<string>();
        while (id && !climbed.has(id)) {
          climbed.add(id);
          shown.add(id);
          id = parentOf.get(id);
        }
        const below = [...(childrenOf.get(match.id) ?? [])];
        const descended = new Set<string>();
        while (below.length > 0) {
          const c = below.pop()!;
          if (descended.has(c.id)) continue;
          descended.add(c.id);
          shown.add(c.id);
          below.push(...(childrenOf.get(c.id) ?? []));
        }
      });
    return shown;
  }, [query, nodes, childrenOf]);

  const mismatchCount = nodes.reduce((sum, n) => sum + n.memberProducts.filter((p) => p.matchesGroupComposition === false).length, 0);
  const assignedCount = nodes.reduce((sum, n) => sum + n.memberProducts.length, 0);

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderProduct = (p: TreeMember, depth: number) => (
    <Box key={p.id} display="flex" alignItems="center" gap={1} sx={{ pl: depth * 3 + 5, pr: 1, py: 0.25 }}>
      <ProductIcon fontSize="small" color="action" />
      <Box minWidth={0}>
        <Typography variant="body2" component="span" fontWeight={productMatches(p) ? 700 : 400}>
          {p.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
          {p.activeIngredientDetails.length > 0 ? p.activeIngredientDetails.join('; ') : 'no active ingredients'}
        </Typography>
      </Box>
      {p.matchesGroupComposition === false && (
        <Tooltip title="This product's active ingredients (ingredient, amount or unit) differ from its group's">
          <WarningIcon fontSize="small" color="warning" />
        </Tooltip>
      )}
    </Box>
  );

  // flat: list the group's own products only, never its child groups (used
  // for loop groups, where following children could go round forever).
  const renderGroup = (node: TreeNode, depth: number, flat = false): React.ReactNode => {
    const allChildren = flat ? [] : childrenOf.get(node.id) ?? [];
    const children = visible ? allChildren.filter((c) => visible.has(c.id)) : allChildren;
    const hasContent = children.length > 0 || node.memberProducts.length > 0;
    const open = !!query || !collapsed.has(node.id);
    const total = subtreeProductCount.get(node.id) ?? node.memberProducts.length;
    const isLeafWithoutIngredients = (childrenOf.get(node.id) ?? []).length === 0 && node.activeIngredientDetails.length === 0;
    const parentHidden = !!node.parentProductGroupId && !nodes.some((n) => n.id === node.parentProductGroupId);

    return (
      <Box key={node.id}>
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{ pl: depth * 3, pr: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
        >
          <IconButton
            size="small"
            onClick={() => toggle(node.id)}
            sx={{ visibility: hasContent && !query ? 'visible' : 'hidden' }}
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
          <Box flex={1} minWidth={0} display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
            <Typography variant="body2" fontWeight={600}>
              {node.name}
            </Typography>
            {node.archivedDate && <Chip label="Archived" size="small" sx={chipSx} />}
            {parentHidden && <Chip label="Parent archived" size="small" variant="outlined" sx={chipSx} />}
            {node.activeIngredientDetails.map((ai) => (
              <Chip key={ai} label={ai} size="small" color="primary" variant="outlined" sx={chipSx} />
            ))}
            {isLeafWithoutIngredients && (
              <Tooltip title="No subgroups and no active ingredients, so a record matched to this group reports no g/ha">
                <Chip icon={<WarningIcon />} label="No active ingredients" size="small" color="warning" variant="outlined" sx={chipSx} />
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mx: 1 }}>
            {total !== node.memberProducts.length
              ? `${total} products incl. subgroups`
              : `${node.memberProducts.length} product${node.memberProducts.length === 1 ? '' : 's'}`}
          </Typography>
          {!node.archivedDate && (
            <Tooltip title="Edit Product Group">
              <IconButton size="small" onClick={() => onEdit(node.id)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {open && (
          <>
            {children.map((c) => renderGroup(c, depth + 1))}
            {node.memberProducts.map((p) => renderProduct(p, depth))}
          </>
        )}
      </Box>
    );
  };

  if (loading && nodes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const shownRoots = visible ? roots.filter((r) => visible.has(r.id)) : roots;
  const shownUnreachable = visible ? unreachable.filter((n) => visible.has(n.id)) : unreachable;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
        <Typography variant="body2" color="text.secondary">
          {nodes.length} groups · {assignedCount} products assigned
          {mismatchCount > 0 && (
            <Box component="span" sx={{ color: 'warning.main', ml: 1 }}>
              · {mismatchCount} product{mismatchCount === 1 ? '' : 's'} with different ingredients from their group
            </Box>
          )}
        </Typography>
        <Box display="flex" gap={1}>
          <Button size="small" startIcon={<UnfoldMoreIcon />} onClick={() => setCollapsed(new Set())} disabled={!!query}>
            Expand all
          </Button>
          <Button size="small" startIcon={<UnfoldLessIcon />} onClick={() => setCollapsed(new Set(nodes.map((n) => n.id)))} disabled={!!query}>
            Collapse all
          </Button>
        </Box>
      </Box>
      <Paper variant="outlined">
        {shownRoots.length === 0 && shownUnreachable.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
            {query ? 'No groups or products match the search.' : 'No product groups found.'}
          </Typography>
        ) : (
          shownRoots.map((r) => renderGroup(r, 0))
        )}
      </Paper>
      {shownUnreachable.length > 0 && (
        <Box mt={2}>
          <Alert severity="warning" sx={{ mb: 1 }}>
            These groups sit in a parent loop (each is ultimately its own ancestor), so no top-level group reaches them.
            Edit one to give it a parent outside the loop, or none.
          </Alert>
          <Paper variant="outlined">{shownUnreachable.map((n) => renderGroup(n, 0, true))}</Paper>
        </Box>
      )}
    </Box>
  );
};

export default ProductGroupTree;
